import {Component, OnDestroy, OnInit} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {Apollo} from 'apollo-angular';
import gql from 'graphql-tag';
import {DeleteContent, DeleteContentVariables} from './__generated__/DeleteContent';
import {catchError, mergeMap} from 'rxjs/operators';
import {EMPTY, Subject, Subscription} from 'rxjs';
import {ErrorService} from '../../services/error.service';
import {ToastrService} from 'ngx-toastr';
import {TemplateService} from '../../services/template.service';
import {UntypedFormControl} from '@angular/forms';

const deleteContentMutation = gql`
  mutation DeleteContent($id: ID, $reason: String) {
    deleteContent(id: $id, reason: $reason) {
      content {
        id
        deleted
        deletedReason
      }
      error
    }
  }
`;

@Component({
  selector: 'app-content-delete',
  templateUrl: './content-delete.component.html',
  styleUrls: ['./content-delete.component.scss'],
  standalone: false,
})
export class ContentDeleteComponent implements OnInit, OnDestroy {
  contentId: string | null = null;
  templateItemId: string | null = null;
  reason = new UntypedFormControl();
  validInput = true;
  private deleteContentId$ = new Subject<string>();
  private deleteTemplateId$ = new Subject<string>();
  private subscriptions: Subscription[] = [];

  constructor(
    private bsModalRef: BsModalRef,
    private apollo: Apollo,
    private toastr: ToastrService,
    private templateService: TemplateService,
    private errService: ErrorService,
  ) {}

  ngOnInit(): void {
    const deleteContentItem$ = this.deleteContentId$.pipe(
      mergeMap(contentId =>
        this.apollo.mutate<DeleteContent, DeleteContentVariables>({
          mutation: deleteContentMutation,
          variables: {
            id: contentId,
            reason: this.reason.value,
          },
        }),
      ),
    );

    const deleteContentOfTemplateItem$ = this.deleteTemplateId$.pipe(
      mergeMap(templateItemId =>
        this.templateService.deleteTemplateItemContent$(templateItemId, this.reason.value),
      ),
    );

    this.subscriptions.push(
      deleteContentItem$
        .pipe(
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
        )
        .subscribe(response => {
          const error = response.data?.deleteContent.error;
          if (error) {
            this.toastr.error($localize`An error has occurred: ${error}.`);
          } else {
            this.toastr.success($localize`Content deleted.`);
          }
          this.bsModalRef.hide();
        }),

      deleteContentOfTemplateItem$
        .pipe(
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
        )
        .subscribe(response => {
          const error = response.data?.deleteContentOfTemplateItem.error;
          if (error) {
            this.toastr.error($localize`An error has occurred: ${error}.`);
          } else {
            this.toastr.success($localize`Content deleted.`);
          }
          this.bsModalRef.hide();
        }),
    );
  }

  deleteContent(): void {
    this.validInput = this.reason.value !== '';
    if (!this.validInput) {
      this.toastr.warning($localize`Please provide a reason for deletion.`);
      return;
    }

    if (this.contentId !== null) {
      this.deleteContentId$.next(this.contentId);
    } else if (this.templateItemId !== null) {
      this.deleteTemplateId$.next(this.templateItemId);
    } else {
      this.toastr.error($localize`No item ID found.`);
    }
  }

  closeModal(): void {
    this.bsModalRef.hide();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
